
export type ArrayElement<ArrayType extends readonly unknown[]> = (
    ArrayType extends readonly (infer ElementType)[] ? ElementType : never
);

/**
 * Exhaustiveness check for switch statements and conditional branches.
 * If TypeScript allows this function to be called, it means not all cases were handled.
 * This will cause a compile-time error if a new case is added but not handled.
 *
 * @example
 * switch (value) {
 *     case "A": return handleA();
 *     case "B": return handleB();
 *     default: return assertNever(value);
 * }
 */
export function assertNever(value: never): never {
    throw new Error("Unexpected value: " + String(value));
}

/**
 * Type guard that checks if two values have the exact same runtime type
 * using prototype equality.
 *
 * Returns true only if:
 * - other is an object (not null or primitive)
 * - other has the exact same prototype as instance (not a subclass)
 *
 * @example
 * class Foo {
 *   equals(other: unknown): boolean {
 *     if (other === this) return true;
 *     if (!isSameRuntimeType(this, other)) return false;
 *     // TypeScript now knows 'other' is type Foo
 *     return this.field === other.field;
 *   }
 * }
 */
export function isSameRuntimeType<T extends object>(
    instance: T,
    other: unknown,
): other is T {
    return typeof other === "object"
        && other !== null
        && Object.getPrototypeOf(other) === Object.getPrototypeOf(instance);
}
